import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from "react";
import { ALL_LANGS, LANG_META, type Lang } from "./strings";
import { ensureI18n } from "./site";

const STORAGE_KEY = "site_lang";

function isLang(v: string | null | undefined): v is Lang {
  return !!v && (ALL_LANGS as string[]).includes(v);
}

function detectInitialLang(): Lang {
  if (typeof window === "undefined") return "ru";
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get("lang");
    if (isLang(q)) return q;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* ignore */
  }
  const nav = (navigator.language || "ru").toLowerCase();
  const short = nav.split("-")[0];
  if (isLang(short)) return short;
  if (short === "kk" || short === "uz") return "ru";
  return "en";
}

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  toggleLang: () => void;
};

const LangContext = createContext<Ctx | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang());
  const [, setLocaleTick] = useState(0);

  // Ensure the active locale bundle is loaded (RU is bundled eagerly).
  useEffect(() => {
    let cancelled = false;
    ensureI18n(lang).then(() => { if (!cancelled) setLocaleTick((n) => n + 1); });
    return () => { cancelled = true; };
  }, [lang]);

  const apply = useCallback((l: Lang) => {
    if (typeof document === "undefined") return;
    const meta = LANG_META[l];
    document.documentElement.lang = meta.htmlLang;
    document.documentElement.dir = meta.dir;
  }, []);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
    if (typeof window !== "undefined") {
      try {
        const url = new URL(window.location.href);
        if (l === "ru") url.searchParams.delete("lang");
        else url.searchParams.set("lang", l);
        window.history.replaceState({}, "", url.toString());
      } catch { /* ignore */ }
    }
    apply(l);
  }, [apply]);

  const toggleLang = useCallback(() => {
    setLang(lang === "ru" ? "en" : "ru");
  }, [lang, setLang]);

  useEffect(() => { apply(lang); }, [lang, apply]);

  const value = useMemo(() => ({ lang, setLang, toggleLang }), [lang, setLang, toggleLang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
